import React, { useState } from 'react';
import { Box, Text, Flex, Spinner, Center, Icon, Collapse } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';

// ─── Person Icon ──────────────────────────────────────────────────────────────
const PersonIcon = ({ color = 'blue.400', bg = 'blue.50' }) => (
  <Flex align="center" justify="center" w="28px" h="28px" borderRadius="7px" bg={bg} flexShrink={0}>
    <Icon viewBox="0 0 24 24" w="14px" h="14px" color={color}>
      <path
        fill="currentColor"
        d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"
      />
    </Icon>
  </Flex>
);

// ─── Recursive report node (reporting-manager chain under an exec) ──────────────
const ReportTreeNode = ({ node, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasChildren = node.children?.length > 0;
  const reportCount = hasChildren ? node.children.length : 0;

  return (
    <Box>
      <Flex
        align="center"
        gap={2}
        mb={open && hasChildren ? 2 : 0}
        cursor={hasChildren ? 'pointer' : 'default'}
        onClick={() => hasChildren && setOpen((o) => !o)}
        userSelect="none"
        w="fit-content"
      >
        {hasChildren ? (
          <Icon
            as={open ? ChevronDownIcon : ChevronRightIcon}
            w="16px"
            h="16px"
            color="gray.400"
            _hover={{ color: 'gray.600' }}
            flexShrink={0}
          />
        ) : (
          <Box w="16px" flexShrink={0} />
        )}
        <Flex
          align="center"
          gap={3}
          px={4}
          py={2.5}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="10px"
          _hover={{ borderColor: 'gray.200', boxShadow: 'sm' }}
          transition="all 0.12s"
          cursor="default"
          minW="260px"
          maxW="340px"
        >
          <PersonIcon />
          <Box overflow="hidden">
            <Text fontSize="13px" fontWeight="500" color="gray.700" noOfLines={1} mb="0px">
              {node.name}
            </Text>
            <Text fontSize="11px" color="gray.400" noOfLines={1} mb="0px">
              {node.designation}
              {node.department ? ` · ${node.department}` : ''}
              {hasChildren
                ? ` · ${reportCount} report${reportCount !== 1 ? 's' : ''}`
                : ''}
            </Text>
          </Box>
        </Flex>
      </Flex>

      {hasChildren && (
        <Collapse in={open} animateOpacity>
          <Box ml={8} mt={1}>
            <ConnectedList
              items={node.children}
              renderItem={(child, idx) => (
                <ReportTreeNode
                  node={child}
                  defaultOpen={idx === 0}
                />
              )}
            />
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

// ─── Connected List (vertical + horizontal lines) ─────────────────────────────
const ConnectedList = ({ items, renderItem }) => (
  <Box position="relative">
    <Box position="absolute" left="-20px" top="0" bottom="0" w="1.5px" bg="gray.300" />
    <Flex direction="column" gap={2}>
      {items.map((item, i) => (
        <Box key={item.id ?? i} position="relative">
          <Box
            position="absolute"
            left="-20px"
            top="50%"
            w="18px"
            h="1.5px"
            bg="gray.300"
            transform="translateY(-50%)"
          />
          {renderItem(item, i)}
        </Box>
      ))}
    </Flex>
  </Box>
);

// ─── Member Row (flat fallback / leaves) ───────────────────────────────────────
const MemberRow = ({ user }) => (
  <Flex
    align="center"
    gap={3}
    px={4}
    py={2.5}
    bg="white"
    border="1px solid"
    borderColor="gray.200"
    borderRadius="10px"
    _hover={{ borderColor: 'gray.200', boxShadow: 'sm' }}
    transition="all 0.12s"
    cursor="default"
    minW="260px"
    maxW="340px"
  >
    <PersonIcon />
    <Box overflow="hidden">
      <Text fontSize="13px" fontWeight="500" color="gray.700" noOfLines={1} mb='0px'>
        {user.name}
      </Text>
      <Text fontSize="11px" color="gray.400" noOfLines={1} mb='0px'>
        {user.designation}
        {user.department ? ` · ${user.department}` : ''}
      </Text>
    </Box>
  </Flex>
);

// ─── Exec Group ───────────────────────────────────────────────────────────────

const ExecGroup = ({ exec, members, nestedMembers = [], defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasNested = nestedMembers.length > 0;
  const showBody = hasNested || members.length > 0;

  return (
    <Box ml={6}>
      <Flex
        align="center"
        gap={2}
        mb={open && showBody ? 2 : 0}
        cursor="pointer"
        onClick={() => setOpen((o) => !o)}
        userSelect="none"
        w="fit-content"
      >
        <Icon
          as={open ? ChevronDownIcon : ChevronRightIcon}
          w="16px"
          h="16px"
          color="gray.400"
          _hover={{ color: 'gray.600' }}
        />
        <Flex
          align="center"
          gap={2.5}
          bg="white"
          border="1px solid"
          borderColor="green.100"
          borderRadius="10px"
          px={3}
          py={2}
          _hover={{ borderColor: 'green.200', boxShadow: 'sm' }}
          transition="all 0.12s"
          minW="180px"
        >
          <PersonIcon color="green.400" bg="green.50" />
          <Box>
            <Text fontSize="13px" fontWeight="600" color="gray.700" mb={0} lineHeight="1.3" noOfLines={1}>
              {exec.name}
            </Text>
            <Text fontSize="11px" color="gray.400" mb='0px'>
              {exec.designation} · {members.length} member{members.length !== 1 ? 's' : ''}
            </Text>
          </Box>
        </Flex>
      </Flex>

      <Collapse in={open} animateOpacity>
        <Box ml={8} mt={1}>
          {hasNested ? (
            <ConnectedList
              items={nestedMembers}
              renderItem={(node, idx) => (
                <ReportTreeNode node={node} defaultOpen={idx === 0} />
              )}
            />
          ) : members.length > 0 ? (
            <ConnectedList
              items={members}
              renderItem={(m) => <MemberRow user={m} />}
            />
          ) : (
            <Text fontSize="12px" color="gray.300" fontStyle="italic">No direct reports</Text>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Org Root ─────────────────────────────────────────────────────────────────

const OrgRoot = ({ orgName, totalCount, children }) => {
  const [open, setOpen] = useState(true);

  return (
    <Box>
      <Flex
        align="center"
        gap={2}
        mb={open ? 3 : 0}
        cursor="pointer"
        onClick={() => setOpen((o) => !o)}
        userSelect="none"
        w="fit-content"
      >
        <Icon as={open ? ChevronDownIcon : ChevronRightIcon} w="16px" h="16px" color="gray.400" />
        <Flex
          align="center"
          gap={2.5}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="10px"
          px={3}
          py={2}
          _hover={{ borderColor: 'gray.300', boxShadow: 'sm' }}
          transition="all 0.12s"
        >
          <Flex align="center" justify="center" w="28px" h="28px" borderRadius="7px" bg="purple.50" flexShrink={0}>
            <Icon viewBox="0 0 24 24" w="14px" h="14px" color="purple.400">
              <path fill="currentColor" d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3zM5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z" />
            </Icon>
          </Flex>
          <Box>
            <Text fontSize="13px" fontWeight="600" color="gray.700" mb={0} lineHeight="1">
              {orgName}
            </Text>
            <Text fontSize="11px" color="gray.400" mb='0px'>{totalCount} members</Text>
          </Box>
        </Flex>
      </Flex>

      <Collapse in={open} animateOpacity>
        <Flex direction="column" gap={4}>{children}</Flex>
      </Collapse>
    </Box>
  );
};

// ─── OrgTree ──────────────────────────────────────────────────────────────────
const OrgTree = ({ execGroups, orphans, totalCount, loading }) => {
  if (loading) {
    return (
      <Center h="40vh" flexDirection="column" gap={4}>
        <Spinner size="lg" color="blue.400" thickness="3px" />
        <Text color="gray.400" fontSize="13px">Loading Teams...</Text>
      </Center>
    );
  }

  return (
    <Box
      maxH="calc(100vh - 200px)"
      overflowY="auto"
      pr={2}
      sx={{
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-track': { bg: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bg: 'gray.200', borderRadius: 'full' },
        '&::-webkit-scrollbar-thumb:hover': { bg: 'gray.300' },
      }}
    >
      <OrgRoot orgName="Nubax Org" totalCount={totalCount}>

        {execGroups.map((g, i) => (
          <ExecGroup
            key={g.exec.id}
            exec={g.exec}
            members={g.members}
            nestedMembers={g.nestedMembers}
            defaultOpen={i === 0}
          />
        ))}

        {orphans.length > 0 && (
          <Box ml={6}>
            <Text
              fontSize="11px"
              color="gray.300"
              mb={2}
              ml={7}
              fontWeight="500"
              textTransform="uppercase"
              letterSpacing="0.05em"
            >
              Unassigned
            </Text>
            <Box ml={8}>
              <ConnectedList
                items={orphans}
                renderItem={(u) => <MemberRow user={u} />}
              />
            </Box>
          </Box>
        )}

      </OrgRoot>
    </Box>
  );
};

export default OrgTree;